import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { AuthContext } from "~/auth/AuthProvider";
import reportDraftFiles from "~/helpers/aiChat/reportDraftFiles";
import { fetchReportDraftAvailability, type ReportDraftAvailability } from "~/services/aiOrchestrator";

const { isChatDraftFile } = reportDraftFiles;

const REPORT_DRAFTS_OFF: ReportDraftAvailability = {
	enabled: false,
	maxFileBytes: 0,
	maxPages: 0,
	remainingThisMonth: 0
};

export interface UseFernieReportDraftOptions {
	isActive: boolean;
}

export interface UseFernieReportDraftReturn {
	attachFile: (file: File) => void;
	availability: ReportDraftAvailability;
	file: File | null;
	isOffered: boolean;
	isStarted: boolean;
	leadsSuggestions: boolean;
	reset: () => void;
	runKey: number;
	start: () => void;
	takesChatFile: (name: string) => boolean;
}

const useFernieReportDraft = ({ isActive }: UseFernieReportDraftOptions): UseFernieReportDraftReturn => {
	const { wsUserType } = useContext(AuthContext);
	const { pathname } = useLocation();
	const [availability, setAvailability] = useState(REPORT_DRAFTS_OFF);
	const [isStarted, setIsStarted] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [runKey, setRunKey] = useState(0);
	const canAsk = isActive && wsUserType !== "customer";

	useEffect(() => {
		if (!canAsk) return;
		let isCancelled = false;
		fetchReportDraftAvailability().then((next) => {
			if (!isCancelled) setAvailability(next);
		});
		return () => {
			isCancelled = true;
		};
	}, [canAsk]);

	const isOffered = canAsk && availability.enabled;

	const start = () => {
		setIsStarted(true);
		setFile(null);
	};

	const attachFile = (attached: File) => {
		setIsStarted(true);
		setFile(attached);
		setRunKey((key) => key + 1);
	};

	const reset = () => {
		setIsStarted(false);
		setFile(null);
	};

	return {
		attachFile,
		availability,
		file,
		isOffered,
		isStarted,
		leadsSuggestions: pathname.startsWith("/reports"),
		reset,
		runKey,
		start,
		takesChatFile: (name: string) => isOffered && isChatDraftFile(name)
	};
};

export default useFernieReportDraft;
